<template>
  <div class="flex flex-col">
    <!-- Loading state -->
    <div v-if="loading" class="py-6 text-center">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-gray-300 animate-spin" />
      <span class="text-sm text-gray-400 ml-2">Loading comments...</span>
    </div>

    <template v-else>
      <!-- Load earlier button -->
      <button
        v-if="hasMore"
        class="text-xs text-primary-500 hover:text-primary-700 py-2 text-center"
        :disabled="loadingMore"
        @click="loadEarlier"
      >
        <FontAwesomeIcon v-if="loadingMore" :icon="['fas', 'spinner']" class="animate-spin mr-1" />
        {{ loadingMore ? 'Loading...' : 'Load earlier comments' }}
      </button>

      <!-- Empty / error state -->
      <div v-if="loadError" class="py-6 text-center text-sm text-amber-700">
        Couldn't load comments.
        <button class="underline ml-1" @click="loadComments">Retry</button>
      </div>
      <div v-else-if="!comments.length" class="py-6 text-center text-sm text-gray-400">
        {{ effectiveStaff ? 'No comments yet' : 'No messages yet. Add a comment to start a conversation with our team.' }}
      </div>

      <!-- Comment list -->
      <div ref="commentList" class="space-y-2.5">
        <RequirementCommentItem
          v-for="comment in visibleComments"
          :key="comment.id"
          :comment="comment"
        />
      </div>

      <!-- New comment pill -->
      <button
        v-if="newCommentAvailable && !isNearBottom"
        class="sticky bottom-2 mx-auto mt-2 text-xs bg-primary-500 text-white px-3 py-1 rounded-full shadow-lg hover:bg-primary-600 transition-colors"
        @click="scrollToBottom"
      >
        <FontAwesomeIcon :icon="['fas', 'arrow-down']" class="mr-1" />
        New comment
      </button>
    </template>

    <!-- Composer -->
    <div class="mt-4">
      <RequirementCommentComposer
        ref="composer"
        :requirement-id="requirementId"
        :show-mode-toggle="effectiveStaff"
        :default-mode="customerFacing ? 'reply' : 'internal_note'"
        @comment-posted="onCommentPosted"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import RequirementCommentItem from './RequirementCommentItem.vue';
import RequirementCommentComposer from './RequirementCommentComposer.vue';

const PAGE_SIZE = 50;
const POLL_INTERVAL = 30000;

export default {
  name: 'RequirementCommentThread',
  components: { FontAwesomeIcon, RequirementCommentItem, RequirementCommentComposer },
  props: {
    requirementId: { type: [Number, String], required: true },
    // When a staff user is in "Customer view" preview, force the customer view:
    // hide internal-note comments, the internal-note composer toggle, and the
    // @-mention picker so nothing internal leaks on a screen-share.
    forceCustomerView: { type: Boolean, default: false },
    // Drives the composer's default mode: a customer-facing ticket defaults to
    // a customer reply, an internal one to an internal note.
    customerFacing: { type: Boolean, default: true },
  },
  emits: ['comment-count', 'new-comments'],
  data() {
    return {
      comments: [],
      loadError: false,
      loading: true,
      loadingMore: false,
      totalCount: 0,
      offset: 0,
      newCommentAvailable: false,
      isNearBottom: true,
      pollTimer: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    isStaff() {
      return this.current_user?.is_staff || this.current_user?.is_superuser;
    },
    effectiveStaff() {
      // Real staff AND not previewing as a customer.
      return this.isStaff && !this.forceCustomerView;
    },
    hasMore() {
      return this.comments.length < this.totalCount;
    },
    visibleComments() {
      if (this.effectiveStaff) return this.comments;
      return this.comments.filter((c) => c.source !== 'internal_note');
    },
  },
  watch: {
    requirementId() {
      this.reset();
      this.loadComments();
    },
  },
  mounted() {
    this.loadComments();
    this.startPolling();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.$nextTick(() => this.attachScrollListener());
  },
  beforeUnmount() {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.detachScrollListener();
  },
  methods: {
    // Called by the parent drawer's "Waiting on you" call to action.
    focusComposer() {
      this.$refs.composer?.focus();
    },
    reset() {
      this.comments = [];
      this.totalCount = 0;
      this.offset = 0;
      this.loading = true;
      this.newCommentAvailable = false;
    },
    // The API returns a plain array (no pagination), so the whole thread
    // arrives at once and the count is its length.
    async loadComments() {
      const requirementId = this.requirementId;
      this.loading = true;
      try {
        const { data } = await axios.get('/api/requirement-comments/', {
          params: { requirement: requirementId },
        });
        if (requirementId !== this.requirementId) return;
        const rows = Array.isArray(data) ? data : data.results || [];
        this.comments = rows;
        this.totalCount = rows.length;
        this.offset = rows.length;
        this.loadError = false;
        this.$emit('comment-count', this.totalCount);
      } catch {
        if (requirementId !== this.requirementId) return;
        this.comments = [];
        this.loadError = true;
      } finally {
        if (requirementId === this.requirementId) this.loading = false;
      }
    },
    latestCreatedAt() {
      let latest = null;
      for (const c of this.comments) {
        if (c.created_at && (!latest || c.created_at > latest)) latest = c.created_at;
      }
      return latest;
    },
    async loadEarlier() {
      if (this.loadingMore) return;
      this.loadingMore = true;
      try {
        const newOffset = Math.max(0, this.totalCount - this.comments.length - PAGE_SIZE);
        const limit = this.totalCount - this.comments.length - newOffset;
        const { data } = await axios.get('/api/requirement-comments/', {
          params: {
            requirement: this.requirementId,
            limit,
            offset: newOffset,
          },
        });
        const earlier = data.results || data;
        this.comments = [...earlier, ...this.comments];
        this.$nextTick(() => {
          this.$refs.commentList?.scrollIntoView({ block: 'start' });
        });
      } catch {
        // ignore
      } finally {
        this.loadingMore = false;
      }
    },
    // Append-only poll through the server's `since` filter, so a live thread
    // costs one tiny request a cycle rather than the whole thread.
    async pollForUpdates() {
      if (document.hidden) return;
      const requirementId = this.requirementId;
      const since = this.latestCreatedAt();
      try {
        const { data } = await axios.get('/api/requirement-comments/', {
          params: { requirement: requirementId, ...(since ? { since } : {}) },
        });
        if (requirementId !== this.requirementId) return;
        const rows = Array.isArray(data) ? data : data.results || [];
        const known = new Set(this.comments.map((c) => c.id));
        const fresh = rows.filter((c) => !known.has(c.id));
        if (!fresh.length) return;
        this.comments = [...this.comments, ...fresh];
        this.totalCount = this.comments.length;
        this.offset = this.comments.length;
        this.$emit('comment-count', this.totalCount);
        this.$emit('new-comments', fresh);
        if (this.isNearBottom) {
          this.$nextTick(() => this.scrollToBottom());
        } else {
          this.newCommentAvailable = true;
        }
      } catch {
        // ignore polling errors
      }
    },
    onCommentPosted(newComment) {
      this.comments.push(newComment);
      this.totalCount = this.comments.length;
      this.$emit('comment-count', this.totalCount);
      this.$nextTick(() => this.scrollToBottom());
    },
    // Bring the newest comment into view without dragging the whole drawer
    // to its footer.
    scrollToBottom() {
      const list = this.$refs.commentList;
      const lastChild = list?.lastElementChild;
      lastChild?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      this.newCommentAvailable = false;
      this.isNearBottom = true;
    },
    startPolling() {
      this.pollTimer = setInterval(() => this.pollForUpdates(), POLL_INTERVAL);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    attachScrollListener() {
      let el = this.$el?.parentElement;
      while (el) {
        const style = getComputedStyle(el);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          this._scrollContainer = el;
          el.addEventListener('scroll', this.onScroll);
          return;
        }
        el = el.parentElement;
      }
    },
    detachScrollListener() {
      if (this._scrollContainer) {
        this._scrollContainer.removeEventListener('scroll', this.onScroll);
        this._scrollContainer = null;
      }
    },
    onScroll() {
      const el = this._scrollContainer;
      if (!el) return;
      const threshold = 100;
      this.isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      if (this.isNearBottom) this.newCommentAvailable = false;
    },
    onVisibilityChange() {
      if (document.hidden) {
        this.stopPolling();
      } else {
        this.stopPolling();
        this.pollForUpdates();
        this.startPolling();
      }
    },
  },
};
</script>
